import { BigMoneyPayoutsAdminAPI } from './API'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { GetCurrentUser } from 'iam/SessionStore'
import { collection } from '../../infra/collection'
import { AdminTransactionResource } from './dtos/admin-transactions'
import { AdminTransaction } from './AdminTransaction'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import { DateRange } from '../../infra/FilterControls/DateRange/DateRangeFilter'
import { CancellationToken } from '../../infra/cancellation'
import { TransactionFlag, serializeTransactionFlag } from '../TransactionFlag'

/**
 * Manages payout history from an admin perspective.
 */
export class AdminPayoutHistoryStore {
  /**
   * Transactions.
   */
  readonly transactions = collection<
    AdminTransactionResource,
    AdminTransaction
  >({
    create: () => new AdminTransaction(this.primeUser, this.primeWorkspace),
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly api: BigMoneyPayoutsAdminAPI,
    private readonly primeUser: PrimeUser,
    private readonly primeWorkspace: PrimeWorkspace,
    private readonly getCurrentUser: GetCurrentUser,
  ) {}

  /**
   * Lists historical transactions.
   *
   * @param query
   * @param paging
   * @param cancellationToken
   */
  async listHistoricalTransactions(
    query: ListHistoricalTransactionsQuery,
    paging: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<AdminTransaction>> {
    const response = await this.api.transactions.listHistoricalTransactions(
      {
        page_size: paging.limit,
        page_token: paging.after,
        recipient_user_id: query.recipientUserId,
        workspace_ids: query.workspaceIds?.length
          ? query.workspaceIds?.join(',')
          : undefined,
        date_completed_from: query.dateCompleted?.from ?? undefined,
        date_completed_to: query.dateCompleted?.to ?? undefined,
        search_text: query.searchText,
        flags: query.flags?.map(serializeTransactionFlag).join(','),
        order_by: 'date_completed desc',
      },
      cancellationToken,
    )

    const page = CursorPaged.fromAipCursorPaged(response)
    return CursorPaged.map(page, this.transactions.set)
  }

  /**
   * Downloads the historical transactions as a CSV.
   *
   * @param query
   */
  async downloadHistoricalTransactionsAsCsv(
    query: DownloadHistoricalTransactionsAsCsvQuery = {},
  ): Promise<ReadableStream<Uint8Array>> {
    return await this.api.transactions.downloadHistoricalTransactionsAsCsv({
      workspace_ids: query.workspaceIds?.length
        ? query.workspaceIds?.join(',')
        : undefined,
      recipient_user_id: query.recipientUserId,
      search_text: query.searchText,
      flags: query.flags?.map(serializeTransactionFlag).join(','),
      date_completed_from: query.dateCompleted?.from ?? undefined,
      date_completed_to: query.dateCompleted?.to ?? undefined,
    })
  }
}

/**
 * Lists historical transactions.
 */
export interface ListHistoricalTransactionsQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: DateRange | null
  flags?: TransactionFlag[] | null
  searchText?: string
}

/**
 * Downloads historical transactions as a CSV.
 */
export interface DownloadHistoricalTransactionsAsCsvQuery {
  workspaceIds?: Id[] | null
  recipientUserId?: Id
  dateCompleted?: DateRange | null
  flags?: TransactionFlag[] | null
  searchText?: string
}
